import {isNumber} from '$utils/number';
import {Button, Collapse, List, ListItem} from '@mui/material';
import {useNavigate, useLocation} from 'react-router-dom';
import {useState, useEffect} from 'react';
import {ChevronDownFrontdeskIcon} from '$assets/svgs/index';

interface NavigationListItemProps {
  title: string;
  icon: React.ReactNode;
  sidebarOpen: boolean;
  active: boolean;
  route: string;
  badgeNumber?: number;
  subItems?: {
    title: string;
    route: string;
  }[];
}

function NavigationListItem(props: NavigationListItemProps) {
  const {title, icon, sidebarOpen, active, badgeNumber, route, subItems} =
    props;
  const [dropdownOpen, setDropdownOpen] = useState<boolean>(false);
  const navigate = useNavigate();
  const location = useLocation();
  const pathName = location.pathname;

  const hasSubItems = subItems && subItems.length > 0;

  const isAnySubItemActive =
    hasSubItems && subItems.some(subItem => pathName.includes(subItem.route));

  const buttonClassName = `button p-2 bg-primary w-full min-w-0 ${active ? 'active' : ''}`;

  useEffect(() => {
    if (isAnySubItemActive) {
      setDropdownOpen(true);
    }
  }, [isAnySubItemActive, pathName]);

  const handleButtonClick = () => {
    if (hasSubItems) {
      setDropdownOpen(!dropdownOpen);
    } else {
      navigate(route);
    }
  };

  const handleSubItemClick = (subItemRoute: string) => {
    navigate(subItemRoute);
  };

  const renderBadge = (badge: number | undefined) => {
    return isNumber(badge) && badge > 0 ? (
      <div className="bg-[#d92d20] absolute rounded-lg w-8 h-6 -top-4 -left-5 flex items-center justify-center px-2 py-1 font-normal text-sm text-white">
        {badge}
      </div>
    ) : null;
  };

  const renderBadgeInline = (badge: number | undefined) => {
    return isNumber(badge) && badge > 0 ? (
      <span className="badge px-3">{badge}</span>
    ) : null;
  };

  return (
    <>
      <li className="px-4 py-0 navigation-list-item mb-4">
        <Button className={buttonClassName} onClick={handleButtonClick}>
          <span className="flex items-center gap-4 justify-between w-full">
            <span className="flex items-center gap-4">
              <span className="flex items-center icon-color relative">
                {icon}
                {!sidebarOpen && renderBadge(badgeNumber)}
              </span>
              {sidebarOpen && (
                <span className="flex items-center">{title}</span>
              )}
            </span>
            {sidebarOpen && hasSubItems && (
              <span className="flex items-center ml-2">
                <ChevronDownFrontdeskIcon
                  style={{
                    transform: dropdownOpen ? 'rotate(180deg)' : 'rotate(0deg)',
                    transition: 'transform 0.3s',
                  }}
                />
              </span>
            )}
          </span>
          {sidebarOpen && renderBadgeInline(badgeNumber)}
        </Button>
      </li>

      {hasSubItems && (
        <Collapse in={dropdownOpen} timeout="auto" unmountOnExit>
          <div className="sub-items-container">
            <List component="div" disablePadding>
              {subItems.map(subItem => {
                const isSubItemActive = pathName.includes(subItem.route);

                return (
                  <ListItem
                    key={subItem.title}
                    disablePadding
                    className="pl-6 pr-4 mb-2"
                    onClick={() => handleSubItemClick(subItem.route)}
                  >
                    <Button
                      className={`button sub-item-button p-2 bg-primary w-full min-w-0 text-left justify-start ${isSubItemActive ? 'active-sub-item' : ''}`}
                    >
                      <span>{subItem.title}</span>
                    </Button>
                  </ListItem>
                );
              })}
            </List>
          </div>
        </Collapse>
      )}
    </>
  );
}

export default NavigationListItem;
